import { Button, Drawer, SkeletonLoader } from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import {
  Entity,
  ExploreInsightCondition,
  ExploreInsightDirection,
} from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DrawerContentWrapper } from '~/components/drawer-content-wrapper/DrawerContentWrapper';
import { useInsightsFullDrawer } from '~/hooks/use-insights-full-drawer/useInsightsFullDrawer';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { EntityHeader } from '~/modules/entity-header/EntityHeader';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { useAssetOverviewNavigation } from '~/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';

import { InsightsInfo } from './insights-info/InsightsInfo';
import { InsightsLeftSection } from './insights-left-section/InsightsLeftSection';
import styles from './InsightsFullDrawer.module.scss';

export interface IconConfig {
  conditions: ExploreInsightCondition[];
  direction: ExploreInsightDirection;
  isCombined?: boolean;
  isMatched?: boolean;
  layout?: 'vertical' | 'horizontal';
}

interface AssetDrawerProps {
  entityTag: string;
}

export const InsightsFullDrawer = ({ entityTag }: AssetDrawerProps) => {
  const { params } = useQueryParams();
  const { t } = useTranslation(['widget']);
  const { closeDrawer } = useInsightsFullDrawer();
  const { data, isLoading } = useEntity(entityTag);
  const entity = data?.data;
  const { navigateToAssetOverview } = useAssetOverviewNavigation(
    data?.data as Entity
  );
  useDisableScroll();

  const [iconConfig, setIconConfig] = useState<IconConfig>();

  return (
    <div data-testid="insights-drawer">
      <Drawer
        className={styles.styledDrawer}
        isOpen
        handleCloseClick={closeDrawer}
        headerButton={
          <Button
            iconName="Expand"
            label={t('widget:assetOverview.assetOverview')}
            onClick={navigateToAssetOverview}
            variant="empty"
            size="xsmall"
          />
        }
      >
        <DrawerContentWrapper>
          {isLoading ? (
            <SkeletonLoader
              data-testid="insight-header-loading"
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: 40,
                  },
                },
              ]}
            />
          ) : (
            <>
              {entity && (
                <EntityHeader
                  className={styles.styledEntityHeader}
                  entity={entity}
                  isDrawer
                  headerButton={<></>}
                />
              )}
            </>
          )}
          <div className={styles.sectionWrapper}>
            <div className={styles.section}>
              {entity && (
                <InsightsLeftSection
                  entity={entity}
                  activeInsightId={params.activeInsightId ?? undefined}
                  handleSetIconConfig={setIconConfig}
                />
              )}
            </div>
            <div className={styles.section}>
              <InsightsInfo
                insightId={params.activeInsightId}
                iconConfig={iconConfig}
              />
            </div>
          </div>
        </DrawerContentWrapper>
      </Drawer>
    </div>
  );
};
